import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Autocomplete, Button, MenuItem, Stack, TextField } from '@mui/material';
import { MoveStock } from '../../api/dto';
import { moveStockSchema } from '../../utils/zodSchemas';
import { useMoveStock } from '../../api/queries';
import { fr } from '../../i18n/fr';
import { STOCK_STATE_LABEL } from '../../utils/mapping';
import ProductSelectorField from './fields/ProductSelectorField';
import { ProductSelectionItem } from '../../types';

interface Props {
  initialProductId?: string;
  initialProduct?: ProductSelectionItem | null;
  onSubmitted?: () => void;
  stores: Array<{ id: string; name: string }>;
}

const MoveForm: React.FC<Props> = ({
  initialProductId,
  initialProduct,
  onSubmitted,
  stores,
}) => {
  type StoreOption = { label: string; value: string };
  const mutation = useMoveStock();
  const defaultValues = useMemo(
    () => ({
      store_id_from: '',
      store_id_to: '',
      product_id: initialProductId ?? '',
      stock_state: 'available' as const,
      quantity: 1,
    }),
    [initialProductId]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<MoveStock>({
    resolver: zodResolver(moveStockSchema),
    defaultValues,
  });

  useEffect(() => {
    setValue('product_id', initialProductId ?? '');
  }, [initialProductId, setValue]);

  const onSubmit = (values: MoveStock) => {
    const payload: MoveStock = {
      ...values,
      store_id_from: values.store_id_from.trim(),
      store_id_to: values.store_id_to.trim(),
      product_id: values.product_id.trim(),
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        reset({ ...defaultValues, stock_state: values.stock_state });
        onSubmitted?.();
      },
    });
  };

  const storeOptions = useMemo<StoreOption[]>(
    () => stores.map((store) => ({ label: store.name, value: store.id })),
    [stores]
  );

  const formatStoreLabel = (option: StoreOption) => `${option.label} — ${option.value}`;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      aria-label="move-form"
    >
      {mutation.isError && (
        <Alert severity="error">{(mutation.error as Error).message}</Alert>
      )}
      <Controller
        name="store_id_from"
        control={control}
        render={({ field }) => (
          <Autocomplete<StoreOption, false, false, false>
            options={storeOptions}
            value={storeOptions.find((option) => option.value === field.value) ?? null}
            onChange={(_, option) => field.onChange(option?.value ?? '')}
            onBlur={field.onBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Magasin origine"
                error={!!errors.store_id_from}
                helperText={errors.store_id_from?.message}
                fullWidth
                inputRef={field.ref}
              />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={formatStoreLabel}
            renderOption={(props, option) => (
              <li {...props}>
                {formatStoreLabel(option)}
              </li>
            )}
          />
        )}
      />
      <Controller
        name="store_id_to"
        control={control}
        render={({ field }) => (
          <Autocomplete<StoreOption, false, false, false>
            options={storeOptions}
            value={storeOptions.find((option) => option.value === field.value) ?? null}
            onChange={(_, option) => field.onChange(option?.value ?? '')}
            onBlur={field.onBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Magasin destination"
                error={!!errors.store_id_to}
                helperText={errors.store_id_to?.message}
                fullWidth
                inputRef={field.ref}
              />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={formatStoreLabel}
            renderOption={(props, option) => (
              <li {...props}>
                {formatStoreLabel(option)}
              </li>
            )}
          />
        )}
      />
      <ProductSelectorField
        control={control}
        name="product_id"
        label={fr.product}
        error={!!errors.product_id}
        helperText={errors.product_id?.message}
        initialSelection={initialProduct ?? null}
      />
      <TextField select label={fr.stockState} {...register('stock_state')} fullWidth>
        {Object.entries(STOCK_STATE_LABEL).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="number"
        label={fr.quantity}
        inputProps={{ min: 0, step: 1 }}
        {...register('quantity', { valueAsNumber: true })}
        error={!!errors.quantity}
        helperText={errors.quantity?.message}
        fullWidth
      />
      <Button type="submit" variant="contained" disabled={mutation.isPending}>
        {fr.saveMovement}
      </Button>
    </Stack>
  );
};

export default MoveForm;
